/**
 * „Der Baum" (Spec 02a, „Oberfläche · Der Baum") — Grundstücke, Gebäude und
 * Einheiten über drei Ebenen, aus `PropertyTree` (Feature 01) geworden.
 *
 * Wird an **zwei** Stellen gemountet: in der Sidebar ab 768px und als
 * Vollbildliste im Bildschirm *Objekte* darunter — nie beide gleichzeitig
 * (`lib/viewport.ts` entscheidet, welche). Deshalb trägt diese Komponente
 * selbst keine Breakpoint-Klassen; der jeweilige Aufrufer reicht über
 * `className` nur die Rahmengestaltung durch.
 *
 * `property-add` war lange die einzige primäre Aktion des Baums — Gebäude und
 * Einheiten werden nicht aus dem Baum heraus angelegt, sondern „aus dem
 * Kontext" im Kopf des geöffneten Objekts (Spec 02a, „Bildschirm Objekt").
 * Seit Spec 05b kommt `private-area-add` dazu, „neben dem vorhandenen
 * `property-add`" (Spec, „Wo") — derselbe Grund für die Trennung: Die Sheets
 * „Grundstück anlegen" und „Privaten Bereich anlegen" liegen nicht hier,
 * sondern beim Aufrufer. `ObjectTree` (dieses Feature, `features/objects`)
 * kennt `features/properties` nicht (`apps/web/CLAUDE.md`, „features kennen
 * einander nicht") — zusammengesetzt wird in der Route.
 */

import { Link } from '@tanstack/react-router'
import { ChevronDown, ChevronRight, Lock } from 'lucide-react'
import { useState } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { useEntitiesTree } from '@/features/objects/api'
import type { TreeNode } from '@/features/objects/api'
import { isPendingEntityId } from '@/lib/optimistic-entities'
import { useIsDesktopViewport } from '@/lib/viewport'

interface ObjectTreeProps {
  readonly className?: string
  /** Öffnet das Sheet „Grundstück anlegen" beim Aufrufer. */
  readonly onAddProperty: () => void
  /** Öffnet `private-area-sheet` beim Aufrufer (Spec 05b). */
  readonly onAddPrivateArea: () => void
}

/** Statuston der Baumzeile unter 768 px — dieselben Tokens wie in der
 * Objektübersicht und in `OpenItemsList`. Farbe ist nie das einzige Signal:
 * Das Statuswort steht daneben. */
function treeStatusToneClass(value: string): string {
  if (value === 'overdue') return 'text-status-overdue'
  if (value === 'none') return 'text-status-paid'
  return 'text-muted'
}

export function ObjectTree({ className, onAddProperty, onAddPrivateArea }: ObjectTreeProps) {
  const query = useEntitiesTree(undefined)

  return (
    <nav
      aria-label="Objekte"
      data-testid="object-tree"
      className={`border-border bg-surface overflow-y-auto ${className ?? ''}`}
    >
      {query.isPending && <SkeletonRows testId="object-tree-loading" rows={3} />}

      {query.isError && (
        <ErrorState
          testId="object-tree-error"
          retryTestId="object-tree-retry"
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      )}

      {query.isSuccess && query.data.items.length === 0 && (
        <EmptyState
          testId="object-tree-empty"
          title="Noch kein Grundstück angelegt."
          hint="Grundstücke sind die Wurzel, an der alles andere hängt."
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <button
                type="button"
                data-testid="property-add"
                onClick={onAddProperty}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out)"
              >
                Grundstück anlegen
              </button>
              <button
                type="button"
                data-testid="private-area-add"
                onClick={onAddPrivateArea}
                className="border-border text-text hover:bg-bg rounded-card text-body border px-4 transition-colors duration-(--duration-fast) ease-(--ease-out)"
              >
                Privaten Bereich anlegen
              </button>
            </div>
          }
        />
      )}

      {query.isSuccess && query.data.items.length > 0 && (
        <>
          <ul>
            {query.data.items.map((item) => (
              <ObjectTreeRow key={item.id} node={item} depth={0} />
            ))}
          </ul>
          {/* `flex-col` statt `flex-wrap` mit `flex-1` (D-W2,
           * `15-design-reviewer.md`): In der 206-px-Sidebar bleiben zwei
           * `flex-1`-Knöpfe nebeneinander bei ~75 px, „+ Grundstück" bricht
           * um. Untereinander, `w-full` wie vor 05b. */}
          <div className="flex flex-col gap-2 p-(--tree-x)">
            <button
              type="button"
              data-testid="property-add"
              onClick={onAddProperty}
              className="border-border text-text hover:bg-bg rounded-card text-body w-full border px-4 transition-colors duration-(--duration-fast) ease-(--ease-out)"
            >
              + Grundstück
            </button>
            <button
              type="button"
              data-testid="private-area-add"
              onClick={onAddPrivateArea}
              className="border-border text-text hover:bg-bg rounded-card text-body w-full border px-4 transition-colors duration-(--duration-fast) ease-(--ease-out)"
            >
              + Privat
            </button>
          </div>
        </>
      )}
    </nav>
  )
}

interface ObjectTreeRowProps {
  readonly node: TreeNode
  readonly depth: number
}

function ObjectTreeRow({ node, depth }: ObjectTreeRowProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const isPending = isPendingEntityId(node.id)
  // Unter 768 px ist dieser Baum nicht die Sidebar, sondern die Objektliste
  // selbst — dort trägt die Zeile die Typ-A-Anatomie (D-4, Bericht 53).
  //
  // Die Entscheidung fällt in JavaScript, nicht per `md:hidden`: Sonst stünde
  // der Zusatztext in jeder Breite im Dokument, und `toHaveText` auf der Zeile
  // läse ihn mit — genau derselbe Grund, aus dem `useIsDesktopViewport`
  // existiert (siehe `lib/viewport.ts`).
  const isTypeARow = !useIsDesktopViewport()
  // Lädt erst beim ersten Aufklappen und bleibt danach im Cache (Spec 02a,
  // „Der Baum"): `enabled` bleibt `true`, sobald einmal aufgeklappt wurde,
  // auch nach dem Zuklappen — genau das React-Query-Standardverhalten für
  // eine Query, die einmal aktiviert wurde. Eine optimistische Zeile hat nie
  // Kinder (`has_children` steht fest auf `false`) und lädt deshalb nie nach.
  const childrenQuery = useEntitiesTree(node.id, { enabled: isExpanded && !isPending })
  const rowTestId = `${node.kind.value}-row-${node.id}`

  // Die optimistische Zeile beim Weg über „Anlegen und noch eins" (Spec 02b,
  // „Schnellerfassung"): mit dem eingegebenen Namen, in einem Wartezustand,
  // nicht anklickbar. Die Antwort ersetzt sie — entweder durch die echte
  // Zeile (Erfolg) oder gar nicht mehr (Fehler, Zeile nimmt sich zurück).
  if (isPending) {
    return (
      <li>
        <div
          data-testid="object-row-pending"
          aria-busy="true"
          className="text-muted flex h-(--row-h) items-center gap-2 opacity-60"
          style={{ paddingLeft: `calc(var(--tree-x) + ${String(depth * 16)}px + var(--row-h))` }}
        >
          <span className="animate-pulse">{node.label}</span>
        </div>
      </li>
    )
  }

  return (
    <li>
      <div
        className="flex h-(--row-h) items-center"
        style={{ paddingLeft: `calc(var(--tree-x) + ${String(depth * 16)}px)` }}
      >
        {node.has_children ? (
          <button
            type="button"
            data-testid={`object-tree-toggle-${node.id}`}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? `${node.label} einklappen` : `${node.label} aufklappen`}
            onClick={() => {
              setIsExpanded((current) => !current)
            }}
            className="text-muted hover:text-text flex h-(--row-h) w-(--row-h) shrink-0 items-center justify-center transition-colors duration-(--duration-fast) ease-(--ease-out)"
          >
            {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </button>
        ) : (
          <span aria-hidden="true" className="h-(--row-h) w-(--row-h) shrink-0" />
        )}
        <Link
          to={node.kind.value === 'contract' ? '.' : '/objekt/$entityId'}
          params={node.kind.value === 'contract' ? {} : { entityId: node.id }}
          search={(previous) => node.kind.value === 'contract'
            ? { ...previous, contract: node.id, contractFacet: undefined }
            : { tab: undefined }}
          data-testid={rowTestId}
          className="text-body text-text hover:bg-bg flex min-h-(--row-h) min-w-0 flex-1 items-center gap-1.5 pr-(--tree-x) transition-colors duration-(--duration-fast) ease-(--ease-out) md:h-(--row-h)"
        >
          {/* Kennzeichnung im Baum (Spec 05b, „Kennzeichnung im Baum"): kein
           * zweiter Farbton, keine zweite Schriftstärke — das Schlosssymbol
           * ist die einzige Achse. */}
          {node.visibility.value === 'private' && (
            <Lock
              data-testid={`object-row-private-${node.id}`}
              aria-label="Privat"
              className="text-muted size-3.5 shrink-0"
            />
          )}
          {/* `truncate` statt Umbruch: Die Sidebar ist 206px breit, der
              volle Name steht in der Objektansicht. Eine zweizeilige Zeile
              würde die Höhe des Baums unregelmäßig machen.

              Unter 768 px ist dieser Baum aber nicht die Sidebar, sondern die
              Objektliste selbst (siehe `ObjectList.tsx`) — dort trägt die Zeile
              die Typ-A-Anatomie mit Kontext und Betrag, wie iOS sie am Telefon
              schon zeigt (D-4, Bericht 53). Ab `md:` bleibt alles einzeilig. */}
          {isTypeARow ? (
            <>
              <span className="flex min-w-0 flex-1 flex-col justify-center">
                <span className="truncate">{node.overview?.headline ?? node.label}</span>
                {node.overview?.subheadline !== undefined && node.overview.subheadline !== '' && (
                  <span className="text-label text-muted truncate">{node.overview.subheadline}</span>
                )}
              </span>
              {node.overview?.open_display !== null && node.overview?.open_display !== undefined && (
                <span className="shrink-0 text-right">
                  <span data-numeric className="text-body text-text block">{node.overview.open_display}</span>
                  {node.overview.open_status !== null && (
                    <span className={'text-label block ' + treeStatusToneClass(node.overview.open_status.value)}>
                      {node.overview.open_status.label}
                    </span>
                  )}
                </span>
              )}
            </>
          ) : (
            <span className="truncate">{node.label}</span>
          )}
        </Link>
      </div>

      {isExpanded && (
        <ul>
          {childrenQuery.isPending && (
            <div style={{ paddingLeft: `${String((depth + 1) * 16)}px` }}>
              <SkeletonRows testId={`object-tree-loading-${node.id}`} rows={2} />
            </div>
          )}

          {childrenQuery.isError && (
            <div style={{ paddingLeft: `${String((depth + 1) * 16)}px` }}>
              <ErrorState
                testId={`object-tree-error-${node.id}`}
                retryTestId={`object-tree-retry-${node.id}`}
                error={childrenQuery.error}
                onRetry={() => {
                  void childrenQuery.refetch()
                }}
              />
            </div>
          )}

          {childrenQuery.isSuccess &&
            childrenQuery.data.items.map((child) => <ObjectTreeRow key={child.id} node={child} depth={depth + 1} />)}
        </ul>
      )}
    </li>
  )
}
